import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Group, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, unwrap } from '../api/client';
import { discardDraft, getDraft, regenerateDraft, reply } from '../api/gen';
import type { Draft } from '../api/gen';
import { useOrg } from '../api/org';
import { percent } from '../lib/format';

type Outcome = NonNullable<Draft['autosendOutcome']>;

// What the auto-send dial decided about this draft, in the operator's words:
// the gate that held it, so the dial is watched rather than guessed at.
const OUTCOMES: Record<Outcome, { label: string; color: string }> = {
  sent: { label: 'sent by the dial', color: 'green' },
  queued: { label: 'queued to send', color: 'green' },
  send_failed: { label: 'auto-send failed', color: 'red' },
  disabled: { label: 'auto-send off', color: 'gray' },
  below_threshold: { label: 'below threshold', color: 'gray' },
  needs_human: { label: 'held: asked for a person', color: 'gray' },
  intent_not_allowed: { label: 'held: intent not allowed', color: 'gray' },
  human_assigned: { label: 'held: claimed by a person', color: 'gray' },
  not_open: { label: 'held: thread not open', color: 'gray' },
};

// What the agent would say, and the three things a person does with it: send
// it, fix it and send it, or throw it away.
//
// The textarea starts as the draft and is what is sent, which is what makes
// "did a person edit this" a real signal rather than a guess: the server
// compares the two.
export function DraftPanel({ conversationId }: { conversationId: string }) {
  const org = useOrg();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [touched, setTouched] = useState(false);

  const draft = useQuery({
    queryKey: ['draft', conversationId],
    // A conversation with no staged draft is the ordinary case, not an
    // error. It is answered as null rather than thrown: a query that fails
    // keeps its last data, so a draft just sent would stay on screen.
    queryFn: () =>
      unwrap(getDraft({ path: { id: conversationId }, headers: org })).catch((err) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }),
    retry: false,
  });

  // A new draft replaces what is in the box, unless the operator has started
  // typing — losing somebody's half-written answer to a background refresh is
  // the kind of thing that makes people stop trusting a tool.
  useEffect(() => {
    if (!touched && draft.data?.body) setBody(draft.data.body);
  }, [draft.data?.body, touched]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['draft', conversationId] });
    void qc.invalidateQueries({ queryKey: ['messages', conversationId] });
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
  };

  // A sent or discarded draft is gone now, not when the refetch lands: until
  // then the effect above would put its text back into the emptied box.
  const settled = () => {
    qc.setQueryData(['draft', conversationId], null);
    setTouched(false);
    setBody('');
    invalidate();
  };

  const send = useMutation({
    // The draft id says which draft this answer started from, so the server
    // judges that one even if a newer draft landed while the operator typed.
    mutationFn: () =>
      unwrap(
        reply({
          path: { id: conversationId },
          headers: org,
          body: { body, ...(draft.data ? { draftId: draft.data.id } : {}) },
        }),
      ),
    onSuccess: settled,
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: err instanceof ApiError && err.status === 502 ? 'Not delivered' : 'Could not send',
        message: (err as Error).message,
      }),
  });

  const discard = useMutation({
    mutationFn: () => unwrap(discardDraft({ path: { id: draft.data!.id }, headers: org })),
    onSuccess: settled,
  });

  const regenerate = useMutation({
    mutationFn: () => unwrap(regenerateDraft({ path: { id: conversationId }, headers: org })),
    onSuccess: () => {
      setTouched(false);
      invalidate();
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  const staged = draft.data;

  return (
    <Card withBorder padding="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Reply
            </Text>
            {staged ? (
              <Tooltip label="The agent's own estimate that this can go as it is">
                <Badge size="xs" variant="light" color={staged.confidence >= 0.8 ? 'green' : 'gray'}>
                  {percent(staged.confidence)}
                </Badge>
              </Tooltip>
            ) : null}
            {staged?.needsHuman ? (
              <Badge size="xs" color="orange" variant="light">
                asked for a person
              </Badge>
            ) : null}
            {staged?.autosendOutcome && staged.autosendOutcome !== 'needs_human' ? (
              <Tooltip label="What the auto-send dial decided">
                <Badge size="xs" variant="outline" color={OUTCOMES[staged.autosendOutcome].color}>
                  {OUTCOMES[staged.autosendOutcome].label}
                </Badge>
              </Tooltip>
            ) : null}
          </Group>
          <Button
            size="compact-xs"
            variant="subtle"
            loading={regenerate.isPending}
            onClick={() => regenerate.mutate()}
          >
            Draft again
          </Button>
        </Group>

        {staged?.citations.length ? (
          <Alert variant="light" p="xs">
            <Text size="xs" fw={600} mb={2}>
              Grounded in
            </Text>
            {staged.citations.map((c) => (
              <Text key={c.documentId + c.excerpt} size="xs" c="dimmed" lineClamp={2}>
                {c.documentTitle}: {c.excerpt}
              </Text>
            ))}
          </Alert>
        ) : staged ? (
          <Text size="xs" c="dimmed">
            Nothing in the knowledge base matched — this draft is ungrounded.
          </Text>
        ) : null}

        <Textarea
          autosize
          minRows={4}
          maxRows={14}
          placeholder="Write the answer, or wait for the agent's."
          value={body}
          onChange={(e) => {
            setTouched(true);
            setBody(e.currentTarget.value);
          }}
        />

        <Group justify="space-between">
          <Button
            size="compact-sm"
            variant="subtle"
            color="gray"
            disabled={!staged}
            loading={discard.isPending}
            onClick={() => discard.mutate()}
          >
            Discard
          </Button>
          <Button
            size="compact-sm"
            disabled={!body.trim()}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            Send
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
